import apiClient from './apiClient';
import analyticsApiClient from './analyticsApiClient';
import type {
    ReportModule,
    ReportTypeOption,
    Column,
    ColumnDataType,
    ReportConfig,
    DateRange,
    Schedule,
    Recipient,
    FilterSpec,
    OrderSpec,
    ApiDuration,
    ApiFilters,
    TimeRange,
    ApiSchedule,
    ApiRecipient,
    CustomReportApiRequest,
    UpdateReportApiRequest,
    CustomReportApiResponse,
    ReportRunHistory,
    ReportStatus,
    GeneratedReport,
    GeneratedReportDetail,
    GeneratedReportsResponse
} from '../components/reports/custom-builder/types';

const CUSTOM_REPORT_ENDPOINT = '/custom-report';
const GENERATED_REPORT_ENDPOINT = '/generated-report';

export interface ReportTypeApiResponse {
    id: number;
    name: string;
    metric_key: string;
    module: number;
    description: string;
    created_at: string;
    updated_at: string;
}

interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

// Schema endpoint types
export interface ColumnInfo {
    name: string;
    label: string;
    data_type: ColumnDataType;
    sortable: boolean;
    filterable: boolean;
    default_visible: boolean;
    is_mandatory?: boolean;
}

export interface ReportSchema {
    key: string;
    name: string;
    description: string;
    category: string;
    available_columns: ColumnInfo[];
    available_filters: string[];
    requires_scope: boolean;
}

export async function fetchReportTypes(module: ReportModule): Promise<ReportTypeOption[]> {
    const response = await apiClient.get<ApiResponse<ReportTypeApiResponse[]>>(
        `${CUSTOM_REPORT_ENDPOINT}/types`,
        { params: { module } }
    );

    // Transform API response to ReportTypeOption format
    return response.data.data.map(item => ({
        value: item.id.toString(),  // Numeric ID as string - sent to API as report_type
        metricKey: item.metric_key, // Metric key - used for fetching schema
        label: item.name,
        description: item.description,
        defaultColumns: []
    }));
}

export async function fetchReportSchema(reportKey: string): Promise<ReportSchema> {
    const response = await analyticsApiClient.get<ReportSchema>(
        `/custom-reports/${reportKey}/schema`
    );
    return response.data;
}

// Transform schema columns to Column type used in the builder
export function transformSchemaColumns(schema: ReportSchema): Column[] {
    return schema.available_columns.map(col => ({
        id: col.name,
        label: col.label,
        visible: col.is_mandatory ? true : col.default_visible, // Mandatory columns are always visible
        type: mapDisplayType(col.data_type),
        dataType: col.data_type,
        sort: null,
        filterable: col.filterable,
        sortable: col.sortable,
        isMandatory: col.is_mandatory
    }));
}

function mapDisplayType(dataType: ColumnDataType): Column['type'] {
    switch (dataType) {
        case 'integer':
        case 'decimal':
            return 'number';
        case 'date':
        case 'timestamp':
            return 'date';
        case 'boolean':
        case 'string':
        default:
            return 'text';
    }
}

// ============================================
// Transformer Functions
// ============================================

/**
 * Transform frontend DateRange to API Duration format
 * Maps UI date range types to API relative/absolute format
 */
export function transformDateRangeToDuration(dateRange: DateRange): ApiDuration {
    // Handle absolute/custom date ranges
    if (dateRange.type === 'custom' || dateRange.type === 'from_date') {
        return {
            type: 'absolute',
            start_date: dateRange.startDate,
            end_date: dateRange.endDate
        };
    }

    // Map relative date range types to API format
    switch (dateRange.type) {
        case '7d':
            return { type: 'relative', value: '7', unit: 'days' };
        case '30d':
            return { type: 'relative', value: '30', unit: 'days' };
        case 'all_time':
            // All time is represented as a very large relative range
            return { type: 'relative', value: '100', unit: 'years' };
        default:
            return { type: 'relative', value: '30', unit: 'days' };
    }
}

/**
 * Transform API TimeRange to frontend DateRange
 * Handles both relative and absolute time range formats from backend
 */
export function transformDurationToDateRange(timeRange: TimeRange): DateRange {
    // Handle absolute time ranges
    if (timeRange.start && timeRange.end) {
        // Parse ISO 8601 to YYYY-MM-DD
        const startDate = timeRange.start.split('T')[0];
        const endDate = timeRange.end.split('T')[0];
        return {
            type: 'custom',
            startDate,
            endDate
        };
    }

    // Handle relative time ranges
    const { relative } = timeRange;
    if (!relative) {
        return { type: '30d', startDate: '', endDate: '' };
    }

    // Map relative values to UI types
    if (relative === '7d') return { type: '7d', startDate: '', endDate: '' };
    if (relative === '30d') return { type: '30d', startDate: '', endDate: '' };
    if (relative === '90d') return { type: '30d', startDate: '', endDate: '' }; // Map 90d to 30d for now
    if (relative === '365d' || relative === '1y') return { type: '30d', startDate: '', endDate: '' }; // Map to 30d
    if (relative === 'ytd') return { type: '30d', startDate: '', endDate: '' }; // Map ytd to 30d
    if (relative === 'mtd') return { type: '30d', startDate: '', endDate: '' }; // Map mtd to 30d
    if (relative === 'qtd') return { type: '30d', startDate: '', endDate: '' }; // Map qtd to 30d
    if (relative === 'all' || relative.startsWith('100')) {
        return { type: 'all_time', startDate: '', endDate: '' };
    }

    // Default to 30d for unknown relative values
    return { type: '30d', startDate: '', endDate: '' };
}

/**
 * Extract visible column IDs from Column array
 */
export function extractColumnIds(columns: Column[]): string[] {
    return columns.filter(col => col.visible).map(col => col.id);
}

/**
 * Extract filter specifications from Column array as JSONB object
 */
export function extractFilters(columns: Column[]): ApiFilters {
    const filters: ApiFilters = {};
    columns
        .filter(col => col.filter && col.filterOperator)
        .forEach(col => {
            filters[col.id] = {
                operator: col.filterOperator!,
                value: col.filter!
            };
        });
    return filters;
}

/**
 * Convert ApiFilters (JSONB) to FilterSpec array for internal use
 */
export function apiFiltersToFilterSpecs(apiFilters: ApiFilters | null): FilterSpec[] {
    if (!apiFilters) return [];
    return Object.entries(apiFilters).map(([field, spec]) => ({
        field,
        operator: spec.operator as FilterSpec['operator'],
        value: Array.isArray(spec.value) ? spec.value.join(',') : spec.value
    }));
}

/**
 * Extract sort specifications from Column array
 */
export function extractSort(columns: Column[]): OrderSpec[] {
    return columns
        .filter(col => col.sort)
        .map(col => ({
            field: col.id,
            direction: col.sort as 'asc' | 'desc'
        }));
}

/**
 * Transform frontend Schedule to API format
 * Only includes relevant fields based on frequency
 */
export function transformScheduleToApi(schedule: Schedule): ApiSchedule {
    // For "once" frequency, use current UTC time
    let time: string;
    if (schedule.frequency === 'once') {
        const now = new Date();
        const hours = now.getUTCHours().toString().padStart(2, '0');
        const minutes = now.getUTCMinutes().toString().padStart(2, '0');
        time = `${hours}:${minutes}Z`;
    } else {
        time = schedule.time.includes('Z') ? schedule.time : `${schedule.time}Z`;
    }

    const base: ApiSchedule = {
        frequency: schedule.frequency,
        time,
    };

    // Add frequency-specific fields
    if (schedule.frequency === 'weekly') {
        base.dayOfWeek = schedule.dayOfWeek;
    } else if (schedule.frequency === 'monthly') {
        base.monthlyOrdinal = schedule.monthlyOrdinal;
        base.monthlyDayType = schedule.monthlyDayType;
    }

    return base;
}

/**
 * Transform API Schedule to frontend format (remove Z suffix)
 */
export function transformScheduleFromApi(apiSchedule: ApiSchedule): Omit<Schedule, 'recipients'> {
    return {
        frequency: apiSchedule.frequency,
        time: apiSchedule.time.replace('Z', ''),
        dayOfWeek: apiSchedule.dayOfWeek,
        monthlyOrdinal: apiSchedule.monthlyOrdinal,
        monthlyDayType: apiSchedule.monthlyDayType
    };
}

/**
 * Transform frontend Recipients to API format
 * Note: userId should contain the user's user_uuid (bigint as string)
 * For internal recipients, both user_id and email are sent
 */
export function transformRecipientsToApi(recipients: Recipient[]): ApiRecipient[] {
    return recipients.map(r => {
        if (r.isExternal) {
            return { email: r.email, external: true };
        }
        return { user_id: r.userId!, email: r.email, external: false };
    });
}

/**
 * Transform API recipients back to frontend format
 */
export function transformRecipientsFromApi(
    apiRecipients: ApiRecipient[],
    userMap?: Map<string, { name: string; email: string }>
): Recipient[] {
    return apiRecipients.map(r => {
        if (r.external) {
            return { email: r.email!, isExternal: true };
        }
        const userId = r.user_id || '';
        const userInfo = userMap?.get(userId);
        return {
            userId,
            email: userInfo?.email || r.email || '',
            name: userInfo?.name,
            isExternal: false
        };
    });
}

/**
 * Transform full ReportConfig to API request payload
 */
export function transformToApiRequest(config: ReportConfig): CustomReportApiRequest {
    return {
        id: config.id || Date.now().toString(),
        name: config.name,
        description: config.description,
        module: config.module,
        report_type: config.type,
        columns: extractColumnIds(config.columns),
        filters: extractFilters(config.columns),
        sort: extractSort(config.columns),
        duration: transformDateRangeToDuration(config.dateRange),
        schedule: transformScheduleToApi(config.schedule),
        recepients: transformRecipientsToApi(config.schedule.recipients)
    };
}

/**
 * Apply API response data back to Column[]
 * (merge filters/sort from API with full column definitions)
 * Updated to handle new backend format with FilterSpec[] array
 */
export function applyApiDataToColumns(
    schemaColumns: Column[],
    apiColumns: string[],
    apiFilters: FilterSpec[] | null,
    apiSort: OrderSpec[]
): Column[] {
    const sortMap = new Map(apiSort.map(s => [s.field, s.direction]));
    const visibleSet = new Set(apiColumns);
    // Convert array of FilterSpec to Map for easy lookup
    const filterMap = new Map(
        (apiFilters || []).map(f => [f.field, f])
    );

    return schemaColumns.map(col => {
        const filterSpec = filterMap.get(col.id);
        return {
            ...col,
            visible: col.isMandatory || visibleSet.has(col.id),
            filter: filterSpec ? (Array.isArray(filterSpec.value) ? filterSpec.value.join(',') : String(filterSpec.value)) : undefined,
            filterOperator: filterSpec?.operator as Column['filterOperator'],
            sort: sortMap.get(col.id) || null
        };
    });
}

// ============================================
// CRUD API Functions
// ============================================

/**
 * Create a new custom report
 */
export async function createReport(config: ReportConfig): Promise<CustomReportApiResponse> {
    const payload = transformToApiRequest(config);

    const response = await apiClient.post<ApiResponse<CustomReportApiResponse>>(
        CUSTOM_REPORT_ENDPOINT,
        payload
    );
    return response.data.data;
}

/**
 * Get all reports, optionally filtered by module
 */
export async function getReports(module?: ReportModule): Promise<CustomReportApiResponse[]> {
    const response = await apiClient.get<ApiResponse<CustomReportApiResponse[]>>(
        CUSTOM_REPORT_ENDPOINT,
        { params: module ? { module } : undefined }
    );
    return response.data.data;
}

/**
 * Get single report by ID
 */
export async function getReportById(id: string): Promise<CustomReportApiResponse> {
    const response = await apiClient.get<ApiResponse<CustomReportApiResponse>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}`
    );
    return response.data.data;
}

/**
 * Update an existing report
 */
export async function updateReport(id: string, config: ReportConfig): Promise<CustomReportApiResponse> {
    const payload = transformToApiRequest(config);

    const response = await apiClient.put<ApiResponse<CustomReportApiResponse>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}`,
        payload
    );
    return response.data.data;
}

/**
 * Update recipients for an existing report (add/remove)
 */
export async function updateReportRecipients(
    id: string,
    addRecipients: Recipient[],
    removeRecipients: Recipient[]
): Promise<CustomReportApiResponse> {
    const response = await apiClient.put<ApiResponse<CustomReportApiResponse>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}`,
        {
            add_recepients: transformRecipientsToApi(addRecipients),
            remove_recepients: transformRecipientsToApi(removeRecipients),
        }
    );
    return response.data.data;
}

/**
 * Update schedule and recipients for an existing report
 */
export async function updateReportSchedule(
    id: string,
    schedule: Schedule,
    addRecipients: Recipient[],
    removeRecipients: Recipient[]
): Promise<CustomReportApiResponse> {
    const response = await apiClient.put<ApiResponse<CustomReportApiResponse>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}`,
        {
            schedule: transformScheduleToApi(schedule),
            add_recepients: transformRecipientsToApi(addRecipients),
            remove_recepients: transformRecipientsToApi(removeRecipients),
        }
    );
    return response.data.data;
}

/**
 * Delete a report
 */
export async function deleteReport(id: string): Promise<void> {
    await apiClient.delete(`${CUSTOM_REPORT_ENDPOINT}/${id}`);
}

/**
 * Update report status (pause/resume)
 */
export async function updateReportStatus(
    id: string,
    status: ReportStatus
): Promise<CustomReportApiResponse> {
    const response = await apiClient.patch<ApiResponse<CustomReportApiResponse>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}/status`,
        { status }
    );
    return response.data.data;
}

/**
 * Get report run history
 */
export async function getReportHistory(
    id: string,
    limit = 10,
    offset = 0
): Promise<{ data: ReportRunHistory[]; meta: { total: number; limit: number; offset: number } }> {
    const response = await apiClient.get<ApiResponse<ReportRunHistory[]>>(
        `${CUSTOM_REPORT_ENDPOINT}/${id}/history`,
        { params: { limit, offset } }
    );
    return {
        data: response.data.data,
        meta: { total: response.data.data.length, limit, offset }
    };
}

/**
 * Unsubscribe from report emails
 */
export async function unsubscribeFromReport(id: string): Promise<void> {
    await apiClient.post(`${CUSTOM_REPORT_ENDPOINT}/${id}/unsubscribe`);
}

// ============================================
// Live Report Data API
// ============================================

export interface ReportDataQueryConfig {
    filters?: Array<{ field: string; operator: string; value: any }>;
    order_by?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    columns?: string[];
    time_range?: { start?: string; end?: string; relative?: string };
}

export interface ReportDataColumnInfo {
    name: string;
    label: string;
    data_type: string;
    sortable: boolean;
    filterable: boolean;
    default_visible: boolean;
    is_mandatory: boolean;
}

export interface ReportDataResponse {
    report_key: string;
    data: Record<string, any>[];
    columns: ReportDataColumnInfo[];
    metadata: {
        query_time: string;
        row_count: number;
        sql?: string;
    };
}

/**
 * Fetch live report data for a custom report configuration
 */
export async function getReportData(
    reportKey: string,
    queryConfig?: ReportDataQueryConfig
): Promise<ReportDataResponse> {
    const response = await analyticsApiClient.post<ReportDataResponse>(
        `/custom-reports/data`,
        {
            report_key: reportKey,
            query_config: queryConfig
        }
    );
    return response.data;
}

// ============================================
// Generated Reports API (for recipients)
// ============================================

/**
 * Get generated reports for a module (reports you are a recipient of)
 */
export async function getGeneratedReports(
    module: ReportModule,
    limit = 20,
    offset = 0
): Promise<{ data: GeneratedReport[]; total: number; limit: number; offset: number }> {
    const response = await apiClient.get<GeneratedReportsResponse>(
        GENERATED_REPORT_ENDPOINT,
        { params: { module, limit, offset } }
    );
    return {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset
    };
}

/**
 * Get a specific generated report with full details
 */
export async function getGeneratedReportById(id: string | number): Promise<GeneratedReportDetail> {
    const response = await apiClient.get<{ success: boolean; data: GeneratedReportDetail }>(
        `${GENERATED_REPORT_ENDPOINT}/${id}`
    );
    return response.data.data;
}

// ============================================
// Export API object
// ============================================

export const customReportsApi = {
    // Existing - Schema & Types
    fetchReportTypes,
    fetchReportSchema,
    transformSchemaColumns,

    // Transformers
    transformToApiRequest,
    transformDateRangeToDuration,
    transformDurationToDateRange,
    transformScheduleToApi,
    transformScheduleFromApi,
    transformRecipientsToApi,
    transformRecipientsFromApi,
    extractColumnIds,
    extractFilters,
    extractSort,
    applyApiDataToColumns,

    // CRUD
    createReport,
    getReports,
    getReportById,
    updateReport,
    updateReportRecipients,
    updateReportSchedule,
    deleteReport,

    // Status & Actions
    updateReportStatus,
    getReportHistory,
    unsubscribeFromReport,

    // Live Report Data
    getReportData,

    // Generated Reports (for recipients)
    getGeneratedReports,
    getGeneratedReportById,
};

export default customReportsApi;
